import Phaser from 'phaser';
import { COLORS, FONTS, fontStr } from '@/config';
import { sfx } from '@/systems/sfx';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';

export type PrimaryButtonVariant = 'primary' | 'secondary' | 'outline' | 'gull';

export type PrimaryButtonValg = {
  scene: Phaser.Scene;
  x: number;
  y: number;
  width: number;
  height?: number;
  label: string;
  variant?: PrimaryButtonVariant;
  onClick: () => void;
  disabled?: boolean;
  hoverSfx?: boolean;
  fontSize?: number;
  pil?: boolean;
};

type Stil = {
  fill: number;
  textColor: string;
  shadowFarge: number;
  shadowAlpha: number;
  stroke: number;
  strokeWidth: number;
  hover: number;
};

const STILER: Record<PrimaryButtonVariant, Stil> = {
  primary: {
    fill: COLORS.wine,
    textColor: '#ffffff',
    shadowFarge: 0x000000,
    shadowAlpha: 0.32,
    stroke: COLORS.ink,
    strokeWidth: 0,
    hover: 0x6f1f2c,
  },
  secondary: {
    fill: COLORS.oakLight,
    textColor: '#3a2410',
    shadowFarge: 0x000000,
    shadowAlpha: 0.2,
    stroke: COLORS.oakDark,
    strokeWidth: 0,
    hover: 0xe6c08f,
  },
  outline: {
    fill: COLORS.white,
    textColor: '#862736',
    shadowFarge: 0x000000,
    shadowAlpha: 0.12,
    stroke: COLORS.wine,
    strokeWidth: 3,
    hover: 0xf6e6e9,
  },
  gull: {
    fill: 0xb8860b,
    textColor: '#1a1a1a',
    shadowFarge: 0x000000,
    shadowAlpha: 0.32,
    stroke: COLORS.white,
    strokeWidth: 3,
    hover: 0xd9a14b,
  },
};

const DISABLED_STIL: Stil = {
  fill: 0xcfc6b7,
  textColor: '#7a7060',
  shadowFarge: 0x000000,
  shadowAlpha: 0.1,
  stroke: 0xcfc6b7,
  strokeWidth: 0,
  hover: 0xcfc6b7,
};

export class PrimaryButton {
  readonly container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Rectangle;
  private shadow: Phaser.GameObjects.Rectangle;
  private label: Phaser.GameObjects.Text;
  private pil?: Phaser.GameObjects.Text;
  private pulseTween?: Phaser.Tweens.Tween;
  private pilTween?: Phaser.Tweens.Tween;
  private glowTween?: Phaser.Tweens.Tween;
  private erDisabled: boolean;
  private valg: PrimaryButtonValg;

  constructor(valg: PrimaryButtonValg) {
    this.valg = valg;
    const høyde = valg.height ?? 80;
    const stil = valg.disabled ? DISABLED_STIL : STILER[valg.variant ?? 'primary'];
    this.erDisabled = !!valg.disabled;

    const scene = valg.scene;
    this.container = scene.add.container(valg.x, valg.y);

    this.shadow = scene.add
      .rectangle(2, 5, valg.width, høyde, stil.shadowFarge, stil.shadowAlpha)
      .setOrigin(0, 0);
    this.container.add(this.shadow);

    this.bg = scene.add
      .rectangle(0, 0, valg.width, høyde, stil.fill, 1)
      .setOrigin(0, 0);
    if (stil.strokeWidth > 0) this.bg.setStrokeStyle(stil.strokeWidth, stil.stroke, 1);
    this.container.add(this.bg);

    const labelTekst = valg.label;
    this.label = scene.add
      .text(valg.width / 2, høyde / 2, labelTekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(valg.fontSize ?? 26),
        color: stil.textColor,
        fontStyle: '700',
        align: 'center',
        wordWrap: { width: valg.width - 24 },
      })
      .setOrigin(0.5);
    this.container.add(this.label);

    if (valg.pil) {
      this.pil = scene.add
        .text(valg.width - 24, høyde / 2, '→', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(valg.fontSize ?? 26),
          color: stil.textColor,
          fontStyle: '700',
        })
        .setOrigin(1, 0.5);
      this.container.add(this.pil);
    }

    if (!valg.disabled) {
      this.bg.setInteractive({ useHandCursor: true });
      this.bg.on('pointerover', () => {
        this.bg.setFillStyle(stil.hover, 1);
        if (valg.hoverSfx) sfx.play('click');
        this.veksleHoverScale(true);
      });
      this.bg.on('pointerout', () => {
        this.bg.setFillStyle(stil.fill, 1);
        this.veksleHoverScale(false);
      });
      this.bg.on('pointerdown', () => {
        this.veksleActiveScale(true);
      });
      this.bg.on('pointerup', () => {
        this.veksleActiveScale(false);
        valg.onClick();
      });
    }
  }

  pulse(aktiv: boolean): void {
    this.pulseTween?.stop();
    this.pulseTween = undefined;
    this.pilTween?.stop();
    this.pilTween = undefined;
    if (!aktiv) {
      this.container.setScale(1);
      if (this.pil) this.pil.setX(this.valg.width - 24);
      return;
    }
    if (foretrekkerRedusertBevegelse()) return;
    this.pulseTween = this.valg.scene.tweens.add({
      targets: this.container,
      scale: { from: 1.0, to: 1.05 },
      duration: 1500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
    if (this.pil) {
      this.pilTween = this.valg.scene.tweens.add({
        targets: this.pil,
        x: { from: this.valg.width - 24, to: this.valg.width - 18 },
        duration: 700,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }

  glow(varighetMs = 3000): void {
    if (foretrekkerRedusertBevegelse()) return;
    this.glowTween?.stop();
    this.glowTween = this.valg.scene.tweens.add({
      targets: this.shadow,
      alpha: { from: 0.32, to: 0.7 },
      duration: 600,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: Math.max(0, Math.floor(varighetMs / 1200) - 1),
    });
  }

  setDisabled(disabled: boolean): void {
    if (disabled === this.erDisabled) return;
    this.erDisabled = disabled;
    const stil = disabled ? DISABLED_STIL : STILER[this.valg.variant ?? 'primary'];
    this.bg.setFillStyle(stil.fill, 1);
    this.label.setColor(stil.textColor);
    if (this.pil) this.pil.setColor(stil.textColor);
    if (disabled) {
      this.bg.disableInteractive();
    } else {
      this.bg.setInteractive({ useHandCursor: true });
    }
  }

  setLabel(tekst: string): void {
    this.label.setText(tekst);
  }

  bounds(): { x: number; y: number; w: number; h: number } {
    return {
      x: this.container.x,
      y: this.container.y,
      w: this.bg.width,
      h: this.bg.height,
    };
  }

  destroy(): void {
    this.pulseTween?.stop();
    this.pilTween?.stop();
    this.glowTween?.stop();
    this.container.destroy(true);
  }

  private veksleHoverScale(hover: boolean): void {
    if (this.pulseTween && this.pulseTween.isPlaying()) return;
    if (foretrekkerRedusertBevegelse()) return;
    this.valg.scene.tweens.add({
      targets: this.container,
      scale: hover ? 1.02 : 1.0,
      duration: 120,
      ease: 'Sine.easeOut',
    });
    this.valg.scene.tweens.add({
      targets: this.shadow,
      alpha: hover ? 0.45 : 0.32,
      duration: 120,
    });
  }

  private veksleActiveScale(active: boolean): void {
    if (foretrekkerRedusertBevegelse()) return;
    this.valg.scene.tweens.add({
      targets: this.container,
      scale: active ? 0.98 : 1.02,
      duration: 80,
    });
  }
}
